/**
 * Minimal client-error reporting seam (phase C.5).
 *
 * NOTE ON PROVENANCE: the C.5 brief referred to "the errorReporting hook of C.0",
 * but C.0 only shipped the envelope foundation — no such hook exists in the
 * codebase. This module is that missing seam, intentionally tiny: a pluggable
 * sink plus a dev-time console fallback. It is the place to wire a real telemetry
 * backend (Sentry, a `/client-errors/` endpoint, etc.) later without touching
 * call sites.
 *
 * Its first consumer is the version-header contract failure (HTTP 400 with
 * `version_headers_required` / `version_headers_invalid`): that is a *client*
 * bug — the frontend failed to send the headers it is required to send — so it
 * must be reported for diagnosis and NEVER surfaced to the user.
 */
export type ClientErrorReport = {
  /** Stable category for grouping (e.g. `version-headers`). */
  kind: string;
  status?: number;
  code?: string | null;
  message?: string;
  details?: unknown;
};

type ClientErrorSink = (report: ClientErrorReport) => void;

let sink: ClientErrorSink | null = null;

/** Registers the telemetry sink. Pass `null` to detach (used in tests). */
export const setClientErrorSink = (next: ClientErrorSink | null): void => {
  sink = next;
};

/** Reports a non-fatal client-side error. Never throws — reporting must not break the caller. */
export const reportClientError = (report: ClientErrorReport): void => {
  try {
    sink?.(report);
  } catch {
    // A broken sink must never propagate into the request path.
  }

  if (import.meta.env.DEV) {
    console.error('[client-error]', report);
  }
};
